import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'oche-interactive oche-press inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-oche)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-canvas)] disabled:pointer-events-none disabled:opacity-50 active:scale-[0.92] active:brightness-95 active:transition-[transform,filter] active:duration-[var(--duration-instant)]',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-oche)] text-black shadow-sm hover:brightness-105 hover:shadow-[0_4px_14px_color-mix(in_srgb,var(--color-oche)_35%,transparent)]',
        outline:
          'border border-[var(--color-line)] bg-transparent hover:border-[var(--color-oche)]/40 hover:bg-[var(--color-surface)]',
        ghost: 'hover:bg-[var(--color-surface)] hover:text-[var(--color-chalk)]',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
